import { Component, inject, OnInit, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '@app/material.module';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Role, AVAILABLE_PERMISSIONS } from '@app/types/role.type';
import { RoleService } from '@app/services/entities/role.service';
import { UserService } from '@app/services/entities/user.service';
import { Observable, map, startWith } from 'rxjs';

@Component({
    selector: 'app-role-dialog',
    standalone: true,
    imports: [CommonModule, MaterialModule, ReactiveFormsModule],
    template: `
    <h2 mat-dialog-title>{{data ? 'Editar' : 'Crear'}} Rol</h2>
    <mat-dialog-content [formGroup]="form">
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Nombre del Rol</mat-label>
        <input matInput formControlName="name" placeholder="Ej: Administrador">
        @if (form.get('name')?.hasError('required') && form.get('name')?.touched) {
          <mat-error>El nombre es requerido</mat-error>
        }
      </mat-form-field>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Descripción</mat-label>
        <textarea matInput formControlName="description" rows="2" placeholder="Descripción del rol y sus responsabilidades"></textarea>
      </mat-form-field>

      <h3>Permisos</h3>
      <div class="section-actions">
        <p class="hint">Selecciona los permisos que tendrá este rol</p>
        <button mat-stroked-button color="primary" type="button" (click)="addAllPermissions()">
          <mat-icon>done_all</mat-icon>
          Agregar Todos
        </button>
      </div>
      
      <!-- Autocomplete para permisos -->
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Buscar y agregar permisos</mat-label>
        <input matInput 
               #permInput
               [formControl]="permissionSearchControl" 
               [matAutocomplete]="autoPermissions"
               placeholder="Escribe para buscar...">
        <mat-autocomplete #autoPermissions="matAutocomplete" 
                          (optionSelected)="addPermission($event.option.value); permInput.value = ''">
          @for (perm of filteredPermissions$ | async; track perm.key) {
            <mat-option [value]="perm.key">
              <mat-icon>security</mat-icon>
              {{perm.label}}
            </mat-option>
          }
        </mat-autocomplete>
        <mat-hint>{{selectedPermissionsCount()}} permisos seleccionados</mat-hint>
      </mat-form-field>

      <!-- Chips de permisos seleccionados -->
      <div class="chips-container">
        @if (selectedPermissionsCount() === 0) {
          <span class="empty-message">No hay permisos seleccionados</span>
        }
        @for (permKey of form.get('permissions')?.value; track permKey) {
          <mat-chip (removed)="removePermission(permKey)">
            {{getPermissionLabel(permKey)}}
            <button matChipRemove>
              <mat-icon>cancel</mat-icon>
            </button>
          </mat-chip>
        }
      </div>

      <h3>Usuarios Asignados</h3>
      <p class="hint">Selecciona los usuarios que tendrán este rol</p>
      
      <!-- Autocomplete para usuarios -->
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Buscar y agregar usuarios</mat-label>
        <input matInput 
               #userInput
               [formControl]="userSearchControl" 
               [matAutocomplete]="autoUsers"
               placeholder="Escribe nombre o email...">
        <mat-autocomplete #autoUsers="matAutocomplete" 
                          (optionSelected)="addUser($event.option.value); userInput.value = ''"
                          [displayWith]="displayUser">
          @for (user of filteredUsers$ | async; track user.id) {
            <mat-option [value]="user">
              <div class="user-option">
                <mat-icon>person</mat-icon>
                <div class="user-info">
                  <span class="user-name">{{user.name}}</span>
                  <span class="user-email">{{user.email}}</span>
                </div>
              </div>
            </mat-option>
          }
        </mat-autocomplete>
        <mat-hint>{{selectedUsersCount()}} usuarios seleccionados</mat-hint>
      </mat-form-field>

      <!-- Chips de usuarios seleccionados -->
      <div class="chips-container">
        @if (selectedUsersCount() === 0) {
          <span class="empty-message">No hay usuarios asignados</span>
        }
        @for (userId of form.get('users')?.value; track userId) {
          <mat-chip (removed)="removeUser(userId)">
            {{getUserName(userId)}}
            <button matChipRemove>
              <mat-icon>cancel</mat-icon>
            </button>
          </mat-chip>
        }
      </div>

    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Cancelar</button>
      <button mat-raised-button color="primary" (click)="save()" [disabled]="form.invalid || isLoading()">
        @if (isLoading()) {
          <mat-spinner diameter="20"></mat-spinner>
        } @else {
          Guardar
        }
      </button>
    </mat-dialog-actions>
  `,
    styles: [`
    .full-width { 
      width: 100%; 
      margin-bottom: 16px; 
    }
    h3 {
      margin-top: 20px;
      margin-bottom: 8px;
      color: #1976d2;
      font-size: 16px;
    }
    .section-actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
    }
    .hint {
      margin: 0;
      font-size: 12px;
      color: rgba(0,0,0,0.6);
    }
    mat-dialog-content {
      max-height: 70vh;
      overflow-y: auto;
      padding: 20px;
    }
    .chips-container {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 16px;
      min-height: 48px;
      padding: 12px;
      border: 1px dashed #ccc;
      border-radius: 4px;
      background-color: #fafafa;
    }
    .empty-message {
      color: rgba(0,0,0,0.38);
      font-size: 13px;
      font-style: italic;
    }
    .user-option {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 4px 0;
    }
    .user-info {
      display: flex;
      flex-direction: column;
    }
    .user-name {
      font-weight: 500;
      font-size: 14px;
    }
    .user-email {
      font-size: 12px;
      color: rgba(0,0,0,0.6);
    }
    mat-chip {
      background-color: #e3f2fd !important;
      color: #1976d2 !important;
    }
  `]
})
export class RoleDialogComponent implements OnInit {
    fb = inject(FormBuilder);
    roleService = inject(RoleService);
    userService = inject(UserService);
    dialogRef = inject(MatDialogRef<RoleDialogComponent>);
    data: Role | null = inject(MAT_DIALOG_DATA);

    form: FormGroup;
    availablePermissions = AVAILABLE_PERMISSIONS;
    isLoading = this.roleService.isLoadingSignal;

    // Autocomplete controls
    permissionSearchControl = new FormControl('');
    userSearchControl = new FormControl('');

    // Filtered observables for autocomplete
    filteredPermissions$: Observable<typeof AVAILABLE_PERMISSIONS>;
    filteredUsers$: Observable<any[]>;

    selectedPermissionsCount = computed(() => {
        const perms = this.form.get('permissions')?.value || [];
        return perms.length;
    });

    selectedUsersCount = computed(() => {
        const users = this.form.get('users')?.value || [];
        return users.length;
    });

    constructor() {
        this.form = this.fb.group({
            name: ['', Validators.required],
            description: [''],
            permissions: [[]],
            users: [[]]
        });

        // Setup permission autocomplete
        this.filteredPermissions$ = this.permissionSearchControl.valueChanges.pipe(
            startWith(''),
            map(value => this._filterPermissions(value || ''))
        );

        // Setup user autocomplete
        this.filteredUsers$ = this.userSearchControl.valueChanges.pipe(
            startWith(''),
            map(value => this._filterUsers(value))
        );
    }

    ngOnInit() {
        if (this.data) {
            this.form.patchValue({
                name: this.data.name,
                description: this.data.description,
                permissions: this.data.permissions || [],
                users: this.data.users || []
            });
        }
    }

    private _filterPermissions(value: string): typeof AVAILABLE_PERMISSIONS {
        const filterValue = (value || '').toLowerCase();
        const currentPermissions = this.form.get('permissions')?.value || [];

        return this.availablePermissions.filter(perm =>
            !currentPermissions.includes(perm.key) &&
            (perm.label.toLowerCase().includes(filterValue) || perm.key.toLowerCase().includes(filterValue))
        );
    }

    private _filterUsers(value: string | any): any[] {
        // Handle both string input and user object
        let filterValue = '';
        if (typeof value === 'string') {
            filterValue = value.toLowerCase();
        } else if (value && value.name) {
            // User object selected, return empty to close autocomplete
            return [];
        }

        const currentUsers = this.form.get('users')?.value || [];
        const allUsers = this.userService.users();

        return allUsers.filter(user => {
            // Skip if already selected
            if (currentUsers.includes(user.id)) return false;

            // Filter by search term
            if (!filterValue) return true;

            const name = (user.name || '').toLowerCase();
            const email = (user.email || '').toLowerCase();

            return name.includes(filterValue) || email.includes(filterValue);
        });
    }

    addPermission(permissionKey: string) {
        const currentPermissions = this.form.get('permissions')?.value || [];
        if (!currentPermissions.includes(permissionKey)) {
            this.form.patchValue({
                permissions: [...currentPermissions, permissionKey]
            });
        }
        // Clear the input and refocus
        this.permissionSearchControl.setValue('', { emitEvent: true });
    }

    addAllPermissions() {
        const allPermissionKeys = this.availablePermissions.map(p => p.key);
        this.form.patchValue({
            permissions: allPermissionKeys
        });
        this.permissionSearchControl.setValue('');
    }

    removePermission(permissionKey: string) {
        const currentPermissions = this.form.get('permissions')?.value || [];
        this.form.patchValue({
            permissions: currentPermissions.filter((p: string) => p !== permissionKey)
        });
    }

    addUser(user: any) {
        if (!user || !user.id) return;

        const currentUsers = this.form.get('users')?.value || [];
        if (!currentUsers.includes(user.id)) {
            this.form.patchValue({
                users: [...currentUsers, user.id]
            });
        }
        // Clear the input
        this.userSearchControl.setValue('', { emitEvent: true });
    }

    removeUser(userId: string) {
        const currentUsers = this.form.get('users')?.value || [];
        this.form.patchValue({
            users: currentUsers.filter((id: string) => id !== userId)
        });
    }

    getPermissionLabel(key: string): string {
        const perm = this.availablePermissions.find(p => p.key === key);
        return perm ? perm.label : key;
    }

    getUserName(userId: string): string {
        const user = this.userService.users().find(u => u.id === userId);
        return user ? user.name : userId;
    }

    displayUser(user: any): string {
        // Return empty string to clear the input after selection
        return '';
    }

    save() {
        if (this.form.invalid) return;

        const val = this.form.value;
        if (this.data) {
            // Update existing role
            this.roleService.updateRole(this.data.id, val).subscribe({
                next: () => this.dialogRef.close(true),
                error: (err) => console.error('Error updating role:', err)
            });
        } else {
            // Create new role
            this.roleService.createRole(val).subscribe({
                next: () => this.dialogRef.close(true),
                error: (err) => console.error('Error creating role:', err)
            });
        }
    }
}
